import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {toast} from 'react-toastify';
import ApiUtils from '../../utils/apiUtils';
import {
  PARTNER_PROFILES,
  PARTNER_SCOPES,
} from '../../constants/formFields/partnerFields';
import {Field, inputSx} from './partnerFormField';

const emptyForm = {
  name: '',
  contactEmail: '',
  description: '',
  // Defaults to the masked profile: a new partner should start narrow and be
  // widened deliberately, never the other way round.
  profile: PARTNER_PROFILES.B.value,
  scopes: [PARTNER_SCOPES.JOBS_READ.value],
};

const CreatePartnerDialog = (props) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (props.open) setForm(emptyForm);
  }, [props.open]);

  const setField = (key, value) => setForm((prev) => ({...prev, [key]: value}));

  const toggleScope = (scope) => {
    setForm((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter((s) => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const canSubmit = form.name.trim() && form.contactEmail.trim();

  const submit = async () => {
    setSaving(true);
    const result = await ApiUtils.makeApiCall(
      'POST',
      'partners/create',
      JSON.stringify(form)
    );
    setSaving(false);

    if (!result || result.status === 'error') {
      toast(result?.message || 'Could not create the partner.', {type: 'error'});
      return;
    }

    toast(`${form.name} created.`, {type: 'success'});
    props.onCreated(result.data?.partner);
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth={'sm'} fullWidth>
      <DialogTitle>New partner</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Field label={'Partner name'} required>
              <TextField
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                fullWidth
                size={'small'}
                sx={inputSx}
              />
            </Field>
          </Grid>
          <Grid item xs={12}>
            <Field label={'Contact email'} required>
              <TextField
                value={form.contactEmail}
                onChange={(e) => setField('contactEmail', e.target.value)}
                fullWidth
                size={'small'}
                sx={inputSx}
              />
            </Field>
          </Grid>
          <Grid item xs={12}>
            <Field label={'Purpose'}>
              <TextField
                placeholder={'What will they use this data for?'}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                fullWidth
                size={'small'}
                multiline
                rows={2}
                sx={inputSx}
              />
            </Field>
          </Grid>
          <Grid item xs={12}>
            <Field label={'Data profile'}>
              <TextField
                select
                value={form.profile}
                onChange={(e) => setField('profile', e.target.value)}
                fullWidth
                size={'small'}
                sx={inputSx}
              >
                {Object.values(PARTNER_PROFILES).map((profile) => (
                  <MenuItem key={profile.value} value={profile.value}>
                    {profile.label}
                  </MenuItem>
                ))}
              </TextField>
            </Field>
          </Grid>
          <Grid item xs={12}>
            <Alert severity={form.profile === 'A' ? 'warning' : 'info'}>
              {PARTNER_PROFILES[form.profile]?.hint}
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'subtitle2'} sx={{mb: 1}}>Scopes</Typography>
            {Object.values(PARTNER_SCOPES).map((scope) => (
              <FormControlLabel
                key={scope.value}
                control={
                  <Checkbox
                    checked={form.scopes.includes(scope.value)}
                    onChange={() => toggleScope(scope.value)}
                  />
                }
                label={scope.label}
              />
            ))}
            {Object.values(PARTNER_SCOPES)
              .filter((scope) => scope.warn && form.scopes.includes(scope.value))
              .map((scope) => (
                <Alert key={scope.value} severity={'warning'} sx={{mt: 1}}>
                  {scope.hint}
                </Alert>
              ))}
          </Grid>
          <Grid item xs={12}>
            {/*
              Three separate steps on purpose: creating a credential is never
              itself a disclosure, and neither is approving it. Only assigning a
              company gives the partner anything to read.
            */}
            <Alert severity={'info'}>
              The partner is created as <strong>Pending</strong>. Approve it,
              generate a secret, and assign companies before it can read
              anything.
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button variant={'contained'} onClick={submit} disabled={!canSubmit || saving}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreatePartnerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default CreatePartnerDialog;
